import { useMutation } from '@tanstack/react-query';
import {
	ArcType,
	CallbackProperty,
	Cartesian2,
	Cartesian3,
	Cartographic,
	Cesium3DTileset,
	Math as CesiumMath,
	type Entity,
	HeadingPitchRange,
	Matrix4,
	PolylineArrowMaterialProperty,
	ScreenSpaceEventHandler,
	ScreenSpaceEventType,
} from 'cesium';
import { Crosshair, Loader2, Move3d, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SPLAT_TYPE, TILESET_ANCHORS, type TilesetAnchor } from '@/common/data-upload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCesium } from '@/context/CesiumContext';
import { useTilesetLocationEdit } from '@/context/TilesetLocationEditContext';
import { placementAtClick } from '@/helper/map-click-placement';
import { closestAxisParam, GIZMO_AXES, type GizmoAxisKey, getAxisDir } from '@/lib/utils/cesium-gizmo';
import { apiErrorMessage } from '@/queries/common';
import { setTilesetLocation } from '@/queries/data-uploads/set-tileset-location';
import { queryClient } from '@/queries/query.client';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyPrimitive = any;

// The tile server applies its own default when `anchor` is omitted, documented as
// `base` in SetTilesetLocationDto. A stored anchor that is null (record created
// outside this UI) or no longer in the enum (legacy `bottom`) therefore resolves
// to `base` rather than to a value we made up — showing `origin` for a model the
// server anchored on `base` would silently move it on the next save.
const resolveAnchor = (stored: string | null | undefined): TilesetAnchor =>
	TILESET_ANCHORS.includes(stored as TilesetAnchor) ? (stored as TilesetAnchor) : 'base';

/**
 * On-map editor for repositioning a tileset directly in the main Cesium viewer.
 * When a tileset is being edited (via TilesetLocationEditContext), it attaches an
 * XYZ translation gizmo to the tileset primitive already loaded on the map and
 * shows an inputs panel on the right. Dragging an axis (or editing lon/lat) moves
 * the model live; Save persists via the tile server.
 */
export function TilesetLocationEditor() {
	const { t } = useTranslation();
	const { viewer } = useCesium();
	const { editing, stop } = useTilesetLocationEdit();

	const [active, setActive] = useState(false);
	const [lon, setLon] = useState<number | null>(null);
	const [lat, setLat] = useState<number | null>(null);
	const [height, setHeight] = useState(0);
	const [heading, setHeading] = useState(0);
	const [anchor, setAnchor] = useState<TilesetAnchor>('base');
	// "Pick on map": while on, a left click on the map moves the model to the clicked point.
	const [pickOnMap, setPickOnMap] = useState(false);
	const pickOnMapRef = useRef(false);
	pickOnMapRef.current = pickOnMap;
	// Whether the last LEFT_DOWN landed on a gizmo axis: the LEFT_CLICK that follows an
	// axis drag must not be taken as a placement click.
	const lastDownOnAxisRef = useRef(false);

	const tilesetRef = useRef<Cesium3DTileset | null>(null);
	const centerRef = useRef<Cartesian3 | null>(null);
	const originalCenterRef = useRef<Cartesian3 | null>(null);
	// Vector from the anchor point (what lon/lat/height in the form and on the tile
	// server refer to) to the bounding sphere centre (what the gizmo tracks). Half
	// the model's extent, so on a large model the two are hundreds of metres apart.
	const anchorOffsetRef = useRef<Cartesian3>(Cartesian3.ZERO.clone());
	const axisLengthRef = useRef(40);
	const axisEntitiesRef = useRef<Partial<Record<GizmoAxisKey, Entity>>>({});
	const handlerRef = useRef<ScreenSpaceEventHandler | null>(null);
	const draggingAxisRef = useRef<GizmoAxisKey | null>(null);
	const dragStartCenterRef = useRef<Cartesian3 | null>(null);
	const dragAxisDirRef = useRef<Cartesian3 | null>(null);
	const flyTimerRef = useRef<number | null>(null);
	const cameraInitDoneRef = useRef(false);
	// Where the current lon/lat/height values came from. Drag updates are already
	// applied to the model, so the inputs effect must not re-apply them (or fly the
	// camera) when React flushes those state writes — sometimes after mouse up.
	// A map click ('pick') is applied like typed input but without flying the camera.
	const coordSourceRef = useRef<'input' | 'model' | 'pick'>('input');
	// requestRenderMode value to restore after a drag.
	const prevRequestRenderModeRef = useRef<boolean | null>(null);
	// True when the editor itself loaded the tileset (TilesLoader hadn't), so we
	// must remove it on cleanup rather than just resetting its transform.
	const ownedTilesetRef = useRef(false);

	const requestRender = () => viewer?.scene.requestRender();

	/**
	 * A tileset picks up a new modelMatrix in its next update pass, so with
	 * requestRenderMode a single requestRender() draws the *previous* transform.
	 * Request the following frame as well so the model never lags the gizmo.
	 */
	const requestRenderSettled = () => {
		requestRender();
		window.requestAnimationFrame(() => {
			if (viewer && !viewer.isDestroyed()) viewer.scene.requestRender();
		});
	};

	const restoreRequestRenderMode = () => {
		if (prevRequestRenderModeRef.current === null) return;
		if (viewer && !viewer.isDestroyed()) viewer.scene.requestRenderMode = prevRequestRenderModeRef.current;
		prevRequestRenderModeRef.current = null;
	};

	const applyTilesetTransform = () => {
		const tileset = tilesetRef.current;
		if (!tileset || !originalCenterRef.current || !centerRef.current) return;
		const translation = Cartesian3.subtract(centerRef.current, originalCenterRef.current, new Cartesian3());
		tileset.modelMatrix = Matrix4.fromTranslation(translation);
		requestRenderSettled();
	};

	/** Bounding sphere centre -> the anchor point the form and the tile server use. */
	const centerToAnchor = (center: Cartesian3) => Cartesian3.subtract(center, anchorOffsetRef.current, new Cartesian3());

	/** Form coordinates (anchor point) -> the bounding sphere centre the gizmo tracks. */
	const anchorToCenter = (lon: number, lat: number, height: number) =>
		Cartesian3.add(Cartesian3.fromDegrees(lon, lat, height), anchorOffsetRef.current, new Cartesian3());

	const reportCenter = () => {
		if (!centerRef.current) return;
		coordSourceRef.current = 'model';
		const carto = Cartographic.fromCartesian(centerToAnchor(centerRef.current));
		setLon(CesiumMath.toDegrees(carto.longitude));
		setLat(CesiumMath.toDegrees(carto.latitude));
		setHeight(Math.round(carto.height * 100) / 100);
	};

	// Set up the gizmo + drag handler for the tileset being edited
	useEffect(() => {
		if (!viewer || viewer.isDestroyed() || !editing?.id) {
			return;
		}

		let cancelled = false;
		let findTimer: number | null = null;

		const endDrag = () => {
			if (!draggingAxisRef.current) return;
			draggingAxisRef.current = null;
			if (!viewer.isDestroyed()) viewer.scene.screenSpaceCameraController.enableInputs = true;
			restoreRequestRenderMode();
			requestRenderSettled();
		};

		// Cesium binds pointerup to the canvas only, so releasing the button outside
		// it (e.g. over this panel) would leave the drag and camera lock stuck.
		window.addEventListener('pointerup', endDrag);
		window.addEventListener('pointercancel', endDrag);

		const findPrimitive = (): Cesium3DTileset | null => {
			const primitives = viewer.scene.primitives;
			for (let i = 0; i < primitives.length; i++) {
				const p = primitives.get(i) as AnyPrimitive;
				if (p?.uniqueId === `tileset-${editing.id}`) return p as Cesium3DTileset;
			}
			return null;
		};

		const setup = (tileset: Cesium3DTileset) => {
			tilesetRef.current = tileset;
			const bs = tileset.boundingSphere;
			originalCenterRef.current = bs.center.clone();
			centerRef.current = bs.center.clone();
			axisLengthRef.current = Math.max(12, bs.radius * 0.8);

			// The saved lon/lat/height is the anchor point, which sits a fixed distance
			// from the bounding sphere centre. Measure that distance here so the form
			// can show (and save) anchor coordinates while the gizmo keeps working in
			// centre coordinates. Never positioned yet -> the two coincide and the
			// centre is the only position we have.
			const saved = editing.tileset;
			const hasSavedLocation = saved?.lon != null && saved?.lat != null;
			anchorOffsetRef.current = hasSavedLocation
				? Cartesian3.subtract(
						centerRef.current,
						Cartesian3.fromDegrees(saved.lon as number, saved.lat as number, saved.height ?? 0),
						new Cartesian3(),
					)
				: Cartesian3.ZERO.clone();

			// Seed the form from stored metadata, falling back to the model's position
			coordSourceRef.current = 'model';
			if (hasSavedLocation) {
				setLon(saved.lon as number);
				setLat(saved.lat as number);
				setHeight(saved.height ?? 0);
			} else {
				reportCenter();
			}
			setHeading(editing.tileset?.heading ?? 0);
			setAnchor(resolveAnchor(editing.tileset?.anchor));

			// Axis arrows (East/North/Up) driven by centerRef via CallbackProperty
			for (const axis of GIZMO_AXES) {
				axisEntitiesRef.current[axis.key] = viewer.entities.add({
					polyline: {
						positions: new CallbackProperty(() => {
							const center = centerRef.current;
							if (!center) return [];
							const dir = getAxisDir(center, axis.column);
							const end = Cartesian3.add(
								center,
								Cartesian3.multiplyByScalar(dir, axisLengthRef.current, new Cartesian3()),
								new Cartesian3(),
							);
							return [center, end];
						}, false),
						width: 22,
						arcType: ArcType.NONE,
						material: new PolylineArrowMaterialProperty(axis.color),
						depthFailMaterial: new PolylineArrowMaterialProperty(axis.color.withAlpha(0.45)),
					},
				});
			}

			// Dedicated handler so the map's own interactions stay intact
			const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
			handlerRef.current = handler;

			handler.setInputAction((movement: { position: { x: number; y: number } }) => {
				lastDownOnAxisRef.current = false;
				const picked = viewer.scene.pick(movement.position as never);
				const pickedId = picked?.id as Entity | undefined;
				if (!pickedId || !centerRef.current) return;
				const axis = GIZMO_AXES.find((a) => axisEntitiesRef.current[a.key] === pickedId);
				if (!axis) return;
				lastDownOnAxisRef.current = true;
				draggingAxisRef.current = axis.key;
				dragStartCenterRef.current = centerRef.current.clone();
				dragAxisDirRef.current = getAxisDir(centerRef.current, axis.column);
				viewer.scene.screenSpaceCameraController.enableInputs = false;
				// Render every frame while dragging so the tileset tracks the gizmo
				// instead of trailing it by a frame.
				if (prevRequestRenderModeRef.current === null) {
					prevRequestRenderModeRef.current = viewer.scene.requestRenderMode;
					viewer.scene.requestRenderMode = false;
				}
			}, ScreenSpaceEventType.LEFT_DOWN);

			handler.setInputAction((movement: { endPosition: { x: number; y: number } }) => {
				if (!draggingAxisRef.current || !dragStartCenterRef.current || !dragAxisDirRef.current) return;
				const ray = viewer.camera.getPickRay(movement.endPosition as never);
				if (!ray) return;
				const t = closestAxisParam(dragStartCenterRef.current, dragAxisDirRef.current, ray.origin, ray.direction);
				centerRef.current = Cartesian3.add(
					dragStartCenterRef.current,
					Cartesian3.multiplyByScalar(dragAxisDirRef.current, t, new Cartesian3()),
					new Cartesian3(),
				);
				applyTilesetTransform();
				reportCenter();
				requestRender();
			}, ScreenSpaceEventType.MOUSE_MOVE);

			handler.setInputAction(endDrag, ScreenSpaceEventType.LEFT_UP);

			// Pick on map: a plain click (not an axis drag) moves the model's anchor onto
			// the clicked point, height included: keeping the old height (0 on a model
			// never placed) put it underground, away from the click. A click on the model
			// itself reaches the surface behind it. Heading stays as set.
			handler.setInputAction((movement: { position: { x: number; y: number } }) => {
				if (!pickOnMapRef.current || lastDownOnAxisRef.current) return;
				const placement = placementAtClick(
					viewer.scene,
					viewer.camera,
					new Cartesian2(movement.position.x, movement.position.y),
					[tileset],
				);
				if (!placement) return;
				coordSourceRef.current = 'pick';
				setLon(placement.lon);
				setLat(placement.lat);
				if (placement.height != null) setHeight(Math.round(placement.height * 100) / 100);
			}, ScreenSpaceEventType.LEFT_CLICK);

			// Frame the tileset for editing
			viewer.zoomTo(tileset, new HeadingPitchRange(0, CesiumMath.toRadians(-45), 0)).finally(() => {
				cameraInitDoneRef.current = true;
			});

			setActive(true);
			requestRender();
		};

		// Load the tileset ourselves if the map's TilesLoader hasn't (or failed to)
		// added it, so Set Location does not depend on the layer already being loaded.
		const loadOwnTileset = () => {
			const rawUrl = editing.tileset?.url;
			if (!rawUrl) {
				toast.error(t('files.location.noUrl'));
				stop();
				return;
			}
			const version = editing.tileset?.updatedAt ?? String(Date.now());
			const url = `${rawUrl}${rawUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`;
			Cesium3DTileset.fromUrl(url, { maximumScreenSpaceError: 8 })
				.then((tileset) => {
					if (cancelled || viewer.isDestroyed()) return;
					(tileset as AnyPrimitive).uniqueId = `tileset-${editing.id}`;
					(tileset as AnyPrimitive).isLayer = true;
					// A splat needs translucent depth picking to be dragged (helper/translucentDepth)
					(tileset as AnyPrimitive).isSplat = editing.tileset?.type === SPLAT_TYPE;
					viewer.scene.primitives.add(tileset);
					ownedTilesetRef.current = true;
					setup(tileset);
				})
				.catch((error) => {
					console.error('Set Location: failed to load tileset', error);
					toast.error(t('files.location.loadFailed'));
					stop();
				});
		};

		// Poll briefly for a primitive already loaded by TilesLoader; if it never
		// appears (~5s), load it ourselves.
		let attempts = 0;
		const tryFind = () => {
			if (cancelled) return;
			const found = findPrimitive();
			if (found) {
				setup(found);
				return;
			}
			attempts += 1;
			if (attempts < 12) {
				findTimer = window.setTimeout(tryFind, 400);
			} else {
				loadOwnTileset();
			}
		};
		tryFind();

		return () => {
			cancelled = true;
			window.removeEventListener('pointerup', endDrag);
			window.removeEventListener('pointercancel', endDrag);
			if (findTimer) window.clearTimeout(findTimer);
			if (flyTimerRef.current) window.clearTimeout(flyTimerRef.current);
			// Remove gizmo entities
			for (const axis of GIZMO_AXES) {
				const ent = axisEntitiesRef.current[axis.key];
				if (ent && !viewer.isDestroyed()) viewer.entities.remove(ent);
			}
			axisEntitiesRef.current = {};
			restoreRequestRenderMode();
			if (tilesetRef.current && !viewer.isDestroyed()) {
				if (ownedTilesetRef.current) {
					// We loaded it ourselves — remove it (TilesLoader will re-add on refetch)
					viewer.scene.primitives.remove(tilesetRef.current);
				} else {
					// Reset the preview transform (Save reloads a fresh primitive anyway)
					tilesetRef.current.modelMatrix = Matrix4.IDENTITY.clone();
				}
			}
			ownedTilesetRef.current = false;
			if (handlerRef.current && !handlerRef.current.isDestroyed()) handlerRef.current.destroy();
			handlerRef.current = null;
			if (!viewer.isDestroyed()) {
				viewer.scene.screenSpaceCameraController.enableInputs = true;
				viewer.scene.requestRender();
			}
			tilesetRef.current = null;
			centerRef.current = null;
			originalCenterRef.current = null;
			anchorOffsetRef.current = Cartesian3.ZERO.clone();
			draggingAxisRef.current = null;
			cameraInitDoneRef.current = false;
			setPickOnMap(false);
			setActive(false);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewer, editing?.id]);

	// Recenter the model when lon/lat/height are edited via the inputs (not while
	// dragging). Camera follows after the initial framing, debounced.
	useEffect(() => {
		if (!active || !viewer || viewer.isDestroyed()) return;
		if (draggingAxisRef.current) return;
		// Values written by the drag are already on the model; re-applying them here
		// would snap it to the rounded height and fly the camera after the drag.
		if (coordSourceRef.current === 'model') return;
		if (lon == null || lat == null) return;
		centerRef.current = anchorToCenter(lon, lat, height);
		applyTilesetTransform();
		requestRender();

		// A map click already has the target in view; only typed coordinates fly there.
		if (coordSourceRef.current === 'input' && cameraInitDoneRef.current && tilesetRef.current) {
			if (flyTimerRef.current) window.clearTimeout(flyTimerRef.current);
			flyTimerRef.current = window.setTimeout(() => {
				if (tilesetRef.current && !viewer.isDestroyed()) {
					viewer.zoomTo(tilesetRef.current, new HeadingPitchRange(0, CesiumMath.toRadians(-45), 0)).catch(() => {});
				}
			}, 400);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lon, lat, height, active]);

	// Crosshair while picking. The canvas cursor is shared with the map's other tools,
	// so always put it back.
	useEffect(() => {
		if (!viewer || viewer.isDestroyed()) return;
		const canvas = viewer.scene.canvas;
		canvas.style.cursor = pickOnMap ? 'crosshair' : '';
		return () => {
			canvas.style.cursor = '';
		};
	}, [viewer, pickOnMap]);

	const saveApi = useMutation({
		mutationFn: setTilesetLocation,
		onSuccess: async () => {
			toast.success(t('files.location.saved'));
			await queryClient.invalidateQueries({ queryKey: ['data-uploads'] });
			stop();
		},
		onError: (error) => {
			console.error('Set tileset location error:', error);
			// Prefer the backend/tile-server message (e.g. "Tile server rejected the
			// location update: ...") over a generic one.
			toast.error(t('files.location.saveFailed'), {
				description: apiErrorMessage(error),
			});
		},
	});

	if (!editing) return null;

	const isSaving = saveApi.isPending;
	// The anchor the model on the map is currently placed on. Switching away from it
	// changes which point of the model lands on the coordinates below, and only the
	// tile server knows where that point sits inside the model — so the shift cannot
	// be previewed here and only shows up after saving.
	const placedAnchor = resolveAnchor(editing.tileset?.anchor);

	const onSave = () => {
		if (!editing.id || lon == null || lat == null) {
			toast.error(t('files.location.positionRequired'));
			return;
		}
		saveApi.mutate({ id: editing.id, lon, lat, height, heading, anchor });
	};

	return (
		<div className="absolute top-24 right-6 z-50 w-64 rounded-lg border bg-card/95 text-card-foreground shadow-lg backdrop-blur animate-in fade-in slide-in-from-right-4 duration-300">
			<div className="flex items-center justify-between border-b px-3 py-2">
				<div className="flex items-center gap-2 min-w-0">
					<Move3d className="size-4 shrink-0 text-emerald-600" />
					<span className="truncate text-sm font-semibold">{t('files.location.title')}</span>
				</div>
				<Button variant="ghost" size="icon" className="size-6" onClick={stop} disabled={isSaving}>
					<X className="size-4" />
				</Button>
			</div>

			<div className="space-y-3 p-3">
				<p className="truncate text-xs text-muted-foreground" title={editing.name ?? ''}>
					{editing.name}
				</p>

				{!active ? (
					<div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
						<Loader2 className="size-4 animate-spin" />
						{t('files.location.locating')}
					</div>
				) : (
					<>
						<p className="text-[11px] leading-snug text-muted-foreground">
							{pickOnMap ? t('files.location.pickHint') : t('files.location.dragHint')}
						</p>

						<div>
							<div className="flex items-center justify-between">
								<Label className="text-xs text-muted-foreground">{t('common.labels.longitude')}</Label>
								<Button
									type="button"
									variant={pickOnMap ? 'default' : 'outline'}
									size="icon-xs"
									title={pickOnMap ? t('files.location.stopPicking') : t('files.location.pickOnMap')}
									aria-label={pickOnMap ? t('files.location.stopPicking') : t('files.location.pickOnMap')}
									aria-pressed={pickOnMap}
									disabled={isSaving}
									onClick={() => setPickOnMap((value) => !value)}
								>
									<Crosshair />
								</Button>
							</div>
							<Input
								type="number"
								step="any"
								value={lon ?? ''}
								onChange={(e) => {
									coordSourceRef.current = 'input';
									setLon(e.target.value === '' ? null : Number(e.target.value));
								}}
								className="h-8 bg-white"
							/>
						</div>
						<div>
							<Label className="text-xs text-muted-foreground">{t('common.labels.latitude')}</Label>
							<Input
								type="number"
								step="any"
								value={lat ?? ''}
								onChange={(e) => {
									coordSourceRef.current = 'input';
									setLat(e.target.value === '' ? null : Number(e.target.value));
								}}
								className="h-8 bg-white"
							/>
						</div>
						<div>
							<Label className="text-xs text-muted-foreground">{t('common.labels.height')}</Label>
							<Input
								type="number"
								step="any"
								value={height}
								onChange={(e) => {
									coordSourceRef.current = 'input';
									setHeight(Number(e.target.value));
								}}
								className="h-8 bg-white"
							/>
						</div>
						<div>
							<Label className="text-xs text-muted-foreground">{t('files.location.heading')}</Label>
							<Input
								type="number"
								step="any"
								value={heading}
								onChange={(e) => setHeading(Number(e.target.value))}
								className="h-8 bg-white"
							/>
						</div>
						<div>
							<Label className="text-xs text-muted-foreground">{t('files.location.anchor')}</Label>
							<Select value={anchor} onValueChange={(v) => setAnchor(v as TilesetAnchor)}>
								<SelectTrigger className="h-8 w-full bg-white">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{TILESET_ANCHORS.map((a) => (
										<SelectItem key={a} value={a}>
											{a}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{anchor !== placedAnchor && (
								<p className="mt-1 text-[11px] leading-snug text-amber-600">
									{t('files.location.anchorMismatch', { anchor, placedAnchor })}
								</p>
							)}
						</div>

						<div className="flex gap-2 pt-1">
							<Button variant="outline" className="flex-1" onClick={stop} disabled={isSaving}>
								{t('common.actions.cancel')}
							</Button>
							<Button className="flex-1" onClick={onSave} disabled={isSaving || lon == null || lat == null}>
								{isSaving && <Loader2 className="mr-1 size-4 animate-spin" />}
								{isSaving ? t('common.states.saving') : t('common.actions.save')}
							</Button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
